'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'

export const AUTH_READY_EVENT = 'hyptrak:auth-ready'

function HyptrakLaunchLogo() {
  return (
    <svg className="launch-logo" viewBox="0 0 1424.01 299" role="img" aria-label="HYPTRAK">
      <g fill="currentColor">
        <path d="M276.69 249.14v-77.09h-62.05v77.09h-57.67V49.86h57.67v76.21h62.05V49.86h57.67v199.28h-57.67Z" />
        <path d="M405.69 249.14v-62.63L338.82 49.86h62.78l32.85 79.42h2.19l33.14-79.42h60.44l-66.87 134.9v64.38h-57.67Z" />
        <path d="M664.56 59.93c10.66 6.72 18.38 15.57 23.87 26.57 5.26 11 7.88 22.83 7.88 35.48s-2.65 24.67-7.96 35.48c-5.31 10.8-13.38 19.44-24.24 25.91-10.85 6.47-24.5 9.71-40.95 9.71h-30.81v56.06h-57.52V49.86h89.06c16.45 0 30 3.36 40.66 10.07Zm-34.46 40.29c-5.31-4.96-12.39-7.45-21.24-7.45h-16.64v58.4h16.64c5.84 0 10.97-1.14 15.4-3.43 4.43-2.29 7.83-5.6 10.22-9.93 2.38-4.33 3.58-9.61 3.58-15.84 0-9.54-2.65-17.79-7.96-22.75Z" />
        <path d="M746.62 249.14V96.28h-51.54V49.86h160.89v46.43h-51.54v152.86h-57.81ZM999.28 58.25c11.48 5.6 20.15 13.48 25.99 23.65 5.84 10.17 8.76 22.07 8.76 35.7 0 10.8-2.04 20.07-6.13 27.81-4.09 7.74-8.88 13.97-14.38 18.69-5.5 4.72-10.34 8.01-14.53 9.85l39.27 75.19h-63.51l-32.41-67.74h-18.69v67.74h-56.5V49.86h89.5c16.94 0 31.15 2.8 42.63 8.4Zm-51.54 84.17c7.4 0 13.72-2.04 18.98-6.13s7.88-10.37 7.88-18.83-2.68-15.01-8.03-19.34c-5.35-4.33-11.58-6.5-18.69-6.5h-24.24v50.81h24.09Z" />
        <path d="m1035.28 249.14 61.46-199.28h72.41l61.03 199.28h-61.46l-9.78-39.86h-56.5l-10.07 39.86h-57.08Zm94.31-150.67-17.37 69.93h37.23l-16.94-69.93h-2.92ZM1355.1 136.87l68.91 112.27H1357l-43.95-74.46-15.04 18.4v56.06h-57.67V49.86h57.67v80.01h2.63l60.88-80.01h60.59l-67.01 87.01Z" />
        <path d="M138.69 49.35v199.83c-.78-.39-1.39-.65-1.97-.99-17.94-10.38-35.86-20.78-53.81-31.12-1.37-.79-1.91-1.7-1.91-3.5.04-42.87.04-85.74.01-128.61 0-1.62.33-2.59 1.71-3.39 18.08-10.4 36.13-20.89 54.18-31.34.48-.28 1-.49 1.79-.88ZM67.69 85.35v127.09c-.5-.25-.89-.42-1.25-.63-11.41-6.6-22.81-13.22-34.22-19.79-.87-.5-1.21-1.08-1.21-2.23.03-27.26.02-54.53 0-81.79 0-1.03.21-1.65 1.09-2.15 11.5-6.61 22.98-13.28 34.46-19.93.31-.18.63-.31 1.14-.56ZM18.69 117.35v64.74c-.25-.13-.45-.21-.64-.32-5.81-3.36-11.62-6.73-17.43-10.08C.18 171.43 0 171.13 0 170.55c.01-13.89.01-27.78 0-41.67 0-.52.11-.84.55-1.1 5.86-3.37 11.7-6.77 17.55-10.15.16-.09.32-.16.58-.28Z" />
      </g>
    </svg>
  )
}

export function LaunchExperience({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    // The index route immediately chooses its destination from local auth.
    // Keep the launch layer intact across that client navigation.
    if (pathname === '/') return

    let cancelled = false
    let firstFrame = 0
    let secondFrame = 0
    const fontsReady = document.fonts?.ready ?? Promise.resolve()
    const authReady = pathname.startsWith('/us') && document.documentElement.dataset.authReady !== 'true'
      ? new Promise<void>((resolve) => window.addEventListener(AUTH_READY_EVENT, () => resolve(), { once: true }))
      : Promise.resolve()

    Promise.all([fontsReady, authReady]).then(() => {
      if (cancelled) return
      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => {
          if (!cancelled) setReady(true)
        })
      })
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
    }
  }, [pathname])

  return (
    <div className={ready ? 'launch-root launch-ready' : 'launch-root'}>
      <div className="launch-app" aria-hidden={!ready}>{children}</div>
      <div className="launch-screen" role="status" aria-label="Opening HYPTRAK" aria-hidden={ready}>
        <div className="launch-glow" aria-hidden="true" />
        <HyptrakLaunchLogo />
      </div>
    </div>
  )
}
